import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Camera } from "lucide-react";
import { apiGet } from "@/lib/api";
import { SiteHeading } from "@/lib/siteContent";

const Portfolio = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    apiGet("/portfolio", { limit: 24 })
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="py-16 sm:py-24 lg:py-32 bg-[#0A0A0A] relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-sky-500 text-xs tracking-[0.3em] uppercase mb-4 block">
            Our Work
          </span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl text-white mb-3">
            <SiteHeading k="portfolio.title" />
          </h2>
          <p className="text-gray-400 text-sm flex items-center justify-center gap-2">
            <Camera className="w-4 h-4" />
            From real customer reviews & photos
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {items.map((it, i) => (
            <motion.button
              key={`${it.url}-${i}`}
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 8) * 0.04 }}
              whileHover={{ scale: 1.02 }}
              onClick={() => setActive(it)}
              data-testid={`portfolio-item-${i}`}
              className="relative aspect-square rounded-lg overflow-hidden bg-white/5 group"
            >
              {it.kind === "video" ? (
                <video src={it.url} className="w-full h-full object-cover" muted loop />
              ) : (
                <img src={it.url} alt="" className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                <div className="flex items-center gap-1">
                  {[...Array(it.rating || 5)].map((_, j) => (
                    <Star key={j} className="w-2.5 h-2.5 fill-sky-400 text-sky-400" />
                  ))}
                </div>
                <p className="text-white text-xs mt-1 truncate">
                  {it.author_name}
                  {it.source === "google" && <span className="text-sky-300 ml-1">· Google</span>}
                </p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center p-6"
          onClick={() => setActive(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-4xl max-h-[90vh]"
          >
            {active.kind === "video" ? (
              <video src={active.url} controls autoPlay className="max-w-full max-h-[80vh] rounded-xl" />
            ) : (
              <img src={active.url} alt="" className="max-w-full max-h-[80vh] rounded-xl" />
            )}
            <div className="mt-4 text-center text-gray-300 text-sm">
              <p>{active.author_name}</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                {[...Array(active.rating || 5)].map((_, j) => (
                  <Star key={j} className="w-3 h-3 fill-sky-400 text-sky-400" />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default Portfolio;
